import type { EscalationEventAction, EscalationStatus, EscalationStatusFilter } from '../../lib/api/escalations';

/**
 * Every `t()` key this module needs for the closed vocabularies
 * `escalations.ts` carries — the identical shape `requestLabels.ts`
 * documents at length: `as const satisfies Record<...>` is what keeps a
 * template-literal key (`` t(`escalations.status.${x}`) ``) out of this
 * codebase. That form compiles but fails `npm run typecheck` against the
 * typed `t()` overloads `i18next.d.ts` installs, because TypeScript widens
 * the template to `string`, which is not a key `en.json`'s shape admits.
 * These objects are the one place each vocabulary is mapped to a literal
 * key, so every call site reads a literal rather than building one.
 */

export const STATUS_LABEL_KEY = {
  open: 'escalations.status.open',
  in_progress: 'escalations.status.inProgress',
  resolved: 'escalations.status.resolved',
  closed: 'escalations.status.closed',
} as const satisfies Record<EscalationStatus, string>;

/** The status-filter segmented control offers four values, `EscalationStatusFilter`'s own set — `all` included, unlike `STATUS_LABEL_KEY` above, which types only a ROW's own status. */
export const QUEUE_LABEL_KEY = {
  open: 'escalations.queue.open',
  resolved: 'escalations.queue.resolved',
  closed: 'escalations.queue.closed',
  all: 'escalations.queue.all',
} as const satisfies Record<EscalationStatusFilter, string>;

export const QUEUE_EMPTY_KEY = {
  open: 'escalations.empty.open',
  resolved: 'escalations.empty.resolved',
  closed: 'escalations.empty.closed',
  all: 'escalations.empty.all',
} as const satisfies Record<EscalationStatusFilter, string>;

export const EVENT_ACTION_LABEL_KEY = {
  opened: 'escalations.eventAction.opened',
  escalated: 'escalations.eventAction.escalated',
  reassigned: 'escalations.eventAction.reassigned',
  commented: 'escalations.eventAction.commented',
  resolved: 'escalations.eventAction.resolved',
  reopened: 'escalations.eventAction.reopened',
  closed: 'escalations.eventAction.closed',
} as const satisfies Record<EscalationEventAction, string>;

/** The four transition verbs `EscalationActionDialog` offers. */
export type EscalationActionKind = 'advance' | 'resolve' | 'close' | 'comment';

export const ACTION_BUTTON_LABEL_KEY = {
  advance: 'escalations.advance',
  resolve: 'escalations.resolve',
  close: 'escalations.close',
  comment: 'escalations.comment',
} as const satisfies Record<EscalationActionKind, string>;

export const ACTION_TITLE_KEY = {
  advance: 'escalations.advanceTitle',
  resolve: 'escalations.resolveTitle',
  close: 'escalations.closeTitle',
  comment: 'escalations.commentTitle',
} as const satisfies Record<EscalationActionKind, string>;

export const ACTION_SUBMIT_KEY = {
  advance: 'escalations.advanceSubmit',
  resolve: 'escalations.resolveSubmit',
  close: 'escalations.closeSubmit',
  comment: 'escalations.commentSubmit',
} as const satisfies Record<EscalationActionKind, string>;
